import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

interface ImportError {
  row: number;
  error: string;
  data?: any;
}

interface ImportResult {
  success: boolean;
  totalRows: number;
  imported: number;
  skipped: number;
  errors: ImportError[];
  classes: any[];
}

interface PreviewData {
  classname: string;
  classdate: string;
  classLU: number;
  input?: string;
  classlink?: string;
  meetlink?: string;
  classdesc?: string;
  memo?: string;
  islecture?: boolean;
  isRegistered?: boolean;
  took?: boolean;
  classwhen?: string;
  classwhere?: string;
  providerno?: number;
  programno?: number;
  cycleid?: number;
  cost?: number;
  categories?: string[];
}

const BulkImport = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Validate file type
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      
      // Validate file size (5MB max)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/import/classes/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'class-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handlePreview = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setPreviewLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await api.post<{ preview: PreviewData[], totalRows: number }>('/api/import/classes/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreviewData(response.data.preview);
      setShowPreview(true);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to preview CSV file');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setShowPreview(false);
    await handleUpload();
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData([]);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await api.post<ImportResult>('/api/import/classes/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      setFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('csvFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import CSV file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h2">Bulk Import Classes</h1>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => navigate('/classes')}
            >
              ← Back to Classes
            </button>
          </div>

          {/* Instructions Card */}
          <div className="card mb-4">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">📋 How to Import Classes</h5>
            </div>
            <div className="card-body">
              <ol className="mb-3">
                <li>Download the CSV template below</li>
                <li>Fill in your class information (see format below)</li>
                <li>Upload the completed CSV file</li>
                <li>Review the import results</li>
              </ol>

              <h6>Required CSV Columns:</h6>
              <ul className="mb-3">
                <li><strong>classname</strong> - Name of the class</li>
                <li><strong>classdate</strong> - Date in YYYY-MM-DD format (e.g., 2024-03-15)</li>
                <li><strong>classLU</strong> - Learning units (credits) as a number</li>
              </ul>

              <h6>Optional Columns:</h6>
              <ul className="mb-0">
                <li><strong>provider</strong> - Name of the provider/organization</li>
                <li><strong>categories</strong> - Semicolon-separated (e.g., "HSW;Ethics;Sustainability")</li>
                <li><strong>notes</strong> - Additional notes or description</li>
              </ul>
            </div>
          </div>

          {/* Download Template */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Step 1: Download Template</h5>
              <p className="card-text text-muted">
                Download a sample CSV template with example data to get started.
              </p>
              <button 
                className="btn btn-primary"
                onClick={handleDownloadTemplate}
              >
                📥 Download CSV Template
              </button>
            </div>
          </div>

          {/* Upload File */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Step 2: Upload Your CSV</h5>
              <div className="mb-3">
                <input 
                  type="file" 
                  id="csvFileInput"
                  className="form-control" 
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                {file && (
                  <div className="form-text text-success">
                    ✓ Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
              <button 
                className="btn btn-success"
                onClick={handlePreview}
                disabled={!file || previewLoading}
              >
                {previewLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading Preview...
                  </>
                ) : (
                  '�️ Preview & Import'
                )}
              </button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>Error:</strong> {error}
              <button type="button" className="btn-close" onClick={() => setError('')}></button>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="card mb-4">
              <div className={`card-header ${result.imported > 0 ? 'bg-success' : 'bg-warning'} text-white`}>
                <h5 className="mb-0">Import Results</h5>
              </div>
              <div className="card-body">
                <div className="row text-center mb-3">
                  <div className="col-md-3">
                    <div className="h3 text-primary">{result.totalRows}</div>
                    <div className="text-muted small">Total Rows</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h3 text-success">{result.imported}</div>
                    <div className="text-muted small">Imported</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h3 text-warning">{result.skipped}</div>
                    <div className="text-muted small">Skipped</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h3 text-info">{result.classes.length}</div>
                    <div className="text-muted small">Total Classes</div>
                  </div>
                </div>

                {result.imported > 0 && (
                  <div className="alert alert-success">
                    ✓ Successfully imported {result.imported} class{result.imported !== 1 ? 'es' : ''}!
                  </div>
                )}

                {/* Errors */}
                {result.errors.length > 0 && (
                  <div className="mt-3">
                    <h6 className="text-danger">Errors ({result.errors.length}):</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Row</th>
                            <th>Error</th>
                            <th>Data</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.errors.slice(0, 20).map((err, idx) => (
                            <tr key={idx}>
                              <td>{err.row}</td>
                              <td className="text-danger">{err.error}</td>
                              <td><small>{JSON.stringify(err.data)}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.errors.length > 20 && (
                        <div className="text-muted small">
                          Showing first 20 of {result.errors.length} errors
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-3">
                  <button 
                    className="btn btn-primary me-2"
                    onClick={() => navigate('/classes')}
                  >
                    View All Classes
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setResult(null);
                      setError('');
                    }}
                  >
                    Import Another File
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">📋 Preview Import Data ({previewData.length} classes)</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleCancelPreview}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Review the data below before importing.</strong> The table shows how CSV columns map to database fields.
                </div>
                
                <div className="table-responsive" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                  <table className="table table-sm table-bordered table-hover">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>#</th>
                        <th>Class Name</th>
                        <th>Date</th>
                        <th>Credits</th>
                        <th>Input Date</th>
                        <th>Registered</th>
                        <th>Completed</th>
                        <th>Categories</th>
                        <th>Cost</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 50).map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td><small>{row.classname}</small></td>
                          <td><small>{row.classdate}</small></td>
                          <td>{row.classLU}</td>
                          <td><small>{row.input || 'Today'}</small></td>
                          <td>{row.isRegistered ? '✓' : ''}</td>
                          <td>{row.took ? '✓' : ''}</td>
                          <td><small>{row.categories?.join(', ') || '-'}</small></td>
                          <td>{row.cost ? `$${row.cost}` : '-'}</td>
                          <td>{row.classlink ? '🔗' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 50 && (
                    <div className="alert alert-warning">
                      Showing first 50 of {previewData.length} rows. All rows will be imported.
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <h6>Field Mapping:</h6>
                  <ul className="small">
                    <li><strong>classid</strong>: Auto-incremented (CSV value ignored)</li>
                    <li><strong>input/classdate</strong>: Converted from M/D/YYYY or YYYY-MM-DD to Date</li>
                    <li><strong>islecture, isRegistered, took</strong>: Converted to boolean (yes/no, true/false, 1/0)</li>
                    <li><strong>isHSW</strong>: Added to categories[] if true</li>
                    <li><strong>cycleid</strong>: Converted to cycleIDs[] array</li>
                    <li><strong>classlink</strong>: Leading # removed if present</li>
                    <li><strong>cid, classdateid</strong>: Skipped (not imported)</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCancelPreview}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={handleConfirmImport} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Importing...
                    </>
                  ) : (
                    <>✓ Confirm & Import ({previewData.length} classes)</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImport;
